import React, { useState, useEffect } from 'react';
import { T, font, mono } from '../utils/theme';
import * as api from '../utils/api';
import { Card, Sect, Btn, Inp, Sel, SearchBar, StatusBadge, SBadge, IcBack, IcEdit, IcCheck, IcTrash, useIsMobile, matchesSearch } from './UI';
import { money } from '../utils/money';
import DeleteProductModal from './DeleteProductModal';
import DisableProductModal from './DisableProductModal';

export default function AdminQuoting({ lang, products, suppliers, conversations, reload, Lk }) {
  const isMobile = useIsMobile();
  const [search, setSearch] = useState('');
  const [detailId, setDetailId] = useState(null);
  const [assignQty, setAssignQty] = useState('');
  const [orderHistory, setOrderHistory] = useState([]);
  const [qtyFlash, setQtyFlash] = useState(false);
  const [quoteInputs, setQuoteInputs] = useState({});
  const [hoveredId, setHoveredId] = useState(null);
  const [renameVals, setRenameVals] = useState({});
  const [replyTo, setReplyTo] = useState('');
  const [replyText, setReplyText] = useState('');
  const [varBusy, setVarBusy] = useState(null);
  const [varMsg, setVarMsg] = useState(null);
  const [flashVarId, setFlashVarId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [disableConfirm, setDisableConfirm] = useState(null);

  // A version a supplier only suggested is reviewed inside its own item, not as a row of its own, so
  // this list never grows with versions we have not agreed to buy. Once opened to all suppliers it
  // becomes an ordinary quoting row here, because by then we do intend to price and assign it.
  const quotingP = products.filter(p => p.status === 'quoting' && !(p.variant_state === 'provisional' && p.restrict_supplier));
  const awaitingVariants = products.filter(p => p.status === 'quoting' && p.variant_state === 'provisional' && p.restrict_supplier);
  const filtered = quotingP.filter(p => !search || matchesSearch(search, p.name) || matchesSearch(search, p.sku || ''));

  // Load order history when detail opens
  useEffect(() => {
    if (detailId) {
      api.getOrderHistory(detailId).then(setOrderHistory).catch(() => setOrderHistory([]));
    }
  }, [detailId]);

  const doAssign = async (productId, supplierId) => {
    if (!assignQty || parseInt(assignQty) <= 0) {
      setQtyFlash(true);
      setTimeout(() => setQtyFlash(false), 300);
      setTimeout(() => { setQtyFlash(true); setTimeout(() => setQtyFlash(false), 300); }, 400);
      setTimeout(() => { setQtyFlash(true); setTimeout(() => setQtyFlash(false), 300); }, 800);
      return;
    }
    await api.assignProduct(productId, supplierId, parseInt(assignQty));
    setAssignQty('');
    setDetailId(null);
    await reload();
  };

  const doRestrict = async (productId, supplierId) => {
    await api.updateProduct(productId, { restrict_supplier: supplierId || null });
    await reload();
  };

  const doSubmitQuoteFor = async (productId, supplierId, price) => {
    const p = parseFloat(price);
    if (!p || p <= 0) return;
    await api.submitQuote(productId, supplierId, p, true);
    setQuoteInputs(prev => ({ ...prev, [supplierId]: '' }));
    await reload();
  };

  // The best available quote for one row. Versions are never compared with each other: a copy price
  // and an original price are different goods, so each row has its own cheapest supplier.
  const bestFor = (prod) => {
    const rel = suppliers.filter(s => !prod.restrict_supplier || s.id === prod.restrict_supplier);
    let price = null, supplier = null, count = 0;
    rel.forEach(s => {
      const q = prod.quotes?.[s.id];
      if (q?.available && q.price != null) { count++; if (price == null || q.price < price) { price = q.price; supplier = s.id; } }
    });
    return { price, supplier, count, total: rel.length };
  };

  // Opening a suggested version is the whole re-quote: the row is already in quoting, so lifting the
  // restriction puts it in every supplier's list on their next load. Nothing is sent to anybody.
  const sayVar = (ok, text) => { setVarMsg({ ok, text }); setTimeout(() => setVarMsg(null), 7000); };

  const doOpenVariant = async (v) => {
    setVarBusy(v.id);
    try {
      const name = String(renameVals[v.id] !== undefined ? renameVals[v.id] : v.name).trim();
      await api.openVariant(v.id, name);
      setRenameVals(prev => { const n = { ...prev }; delete n[v.id]; return n; });
      const who = suppliers.find(s => s.id === v.proposed_by);
      sayVar(true, lang === 'zh'
        ? `已接受：“${name}” 现在是一个独立产品，正在向所有供应商询价${who ? `，${who.name} 的价格已填入` : ''}。`
        : `Accepted: “${name}” is now a product of its own, out for quoting to every supplier${who ? `, with ${who.name}'s price already in` : ''}.`);
      await reload();
      // Show them where it went, rather than leaving them on a card that quietly lost a row.
      setDetailId(v.id);
      setAssignQty('');
      setFlashVarId(v.id);
      setTimeout(() => setFlashVarId(null), 2600);
    } catch (e) {
      sayVar(false, String(e.message || ''));
    } finally { setVarBusy(null); }
  };

  const doRejectVariant = async (v) => {
    setVarBusy(v.id);
    try {
      await api.rejectVariant(v.id);
      sayVar(true, lang === 'zh'
        ? `“${v.name}” 已移除，未进入产品列表。`
        : `“${v.name}” removed — it never entered the product list.`);
      await reload();
    } catch (e) {
      sayVar(false, String(e.message || ''));
    } finally { setVarBusy(null); }
  };

  // Saving the name on its own, without opening it to everyone: the two are separate decisions and
  // the box needs a way out that keeps what was typed.
  const doRenameVariant = async (v) => {
    const name = String(renameVals[v.id] !== undefined ? renameVals[v.id] : v.name).trim();
    if (!name) return;
    setVarBusy(v.id);
    try {
      await api.updateProduct(v.id, { name });
      setRenameVals(prev => { const n = { ...prev }; delete n[v.id]; return n; });
      sayVar(true, lang === 'zh' ? `名称已保存：${name}` : `Name saved: ${name}`);
      await reload();
    } catch (e) {
      sayVar(false, String(e.message || ''));
    } finally { setVarBusy(null); }
  };

  const sendReply = async (pid) => {
    const text = replyText.trim();
    if (!text || !replyTo) return;
    await api.addConversation({ product_id: pid, author: 'Tan Star', author_type: 'admin', supplier_id: replyTo, text });
    setReplyText('');
    await reload();
  };

  // Detail view
  if (detailId) {
    const p = products.find(x => x.id === detailId);
    if (!p) { setDetailId(null); return null; }
    // Only the product a version was suggested against shows this panel. A version we accepted is an
    // ordinary product from then on and has nothing to review, so it does not carry the panel itself.
    const suggestedFromHere = products.filter(x => x.variant_parent_id === p.id);
    const pendingHere = suggestedFromHere.filter(v => v.variant_state === 'provisional' && v.restrict_supplier);
    const groupMembers = suggestedFromHere.length ? [p, ...suggestedFromHere] : [];
    const prodConvos = (conversations || []).filter(c => c.product_id === p.id);

    const relevantSup = suppliers.filter(s => !p.restrict_supplier || s.id === p.restrict_supplier);
    let bestPrice = Infinity, bestSup = null;
    relevantSup.forEach(s => {
      const q = p.quotes?.[s.id];
      if (q?.available && q.price != null && q.price < bestPrice) { bestPrice = q.price; bestSup = s.id; }
    });

    return (
      <div>
        <Btn variant="ghost" onClick={() => setDetailId(null)} style={{ marginBottom: 14 }}>{IcBack} {Lk('back')}</Btn>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 22 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: T.text }}>{p.name}</h2>
            <div style={{ fontSize: 12, color: T.dim, marginTop: 3 }}>
              {p.sku} · {Lk('soldOut')} {p.untick_date}
              {p.cost_price != null && <span> · {Lk('cost')}: <span style={{ color: T.muted }}>{money(p.cost_price)}</span></span>}
            </div>
          </div>
          {/* Under the badge so an item that turns out not to be wanted can go from here, without
              leaving quoting to find it again in the product list. */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, flexShrink: 0, marginLeft: 12 }}>
            <StatusBadge status={p.status} lang={lang} />
            <Btn variant="dangerSm" onClick={() => setDeleteConfirm(p.id)} style={isMobile ? { minHeight: 44, padding: '0 14px' } : undefined}>{IcTrash} {Lk('delete')}</Btn>
            {/* Below Delete: for an item that still exists but will never be made again. */}
            <Btn variant="ghostSm" onClick={() => setDisableConfirm(p.id)} title={lang === 'zh' ? '停用（已停产）' : 'Disable (discontinued)'} style={isMobile ? { minHeight: 44, padding: '0 14px' } : undefined}>{lang === 'zh' ? '停用' : 'Disable'}</Btn>
          </div>
        </div>

        <DeleteProductModal lang={lang} products={products} productId={deleteConfirm} Lk={Lk}
          onClose={() => setDeleteConfirm(null)}
          onDeleted={async () => { setDeleteConfirm(null); setDetailId(null); await reload(); }} />
        <DisableProductModal lang={lang} products={products} productId={disableConfirm} Lk={Lk}
          onClose={() => setDisableConfirm(null)}
          onDisabled={async () => { setDisableConfirm(null); setDetailId(null); await reload(); }} />

        {/* Outside the versions panel on purpose: accepting a version moves you to the new product,
            which has no versions panel, and the message about what just happened has to come with you. */}
        {varMsg && (
          <div style={{ background: varMsg.ok ? T.successBg : T.dangerBg, border: `1px solid ${varMsg.ok ? T.success : T.danger}55`, borderRadius: 8, padding: '11px 15px', marginBottom: 16, fontSize: 13, fontWeight: 600, color: T.text }}>
            {varMsg.ok ? '✓ ' : ''}{varMsg.text}
          </div>
        )}


        {/* Versions of this item. A supplier's suggestion lands here and nowhere else: until we
            receive stock of it, it is not in the product list, the reorder screen, the export or the
            reports. Suggest ten, order two, and only the two ever reach the catalogue. */}
        {groupMembers.length > 0 && (
          <Sect title={`${lang === 'zh' ? '此产品的版本' : 'Versions of this item'} (${groupMembers.length})`}>
            <style>{`@keyframes mcFlashCard {
              0%, 100% { box-shadow: none; }
              20%, 60% { box-shadow: 0 0 0 3px ${T.success}66; }
            }`}</style>
            {groupMembers.map(v => {
              const b = bestFor(v);
              const isPending = v.variant_state === 'provisional' && !!v.restrict_supplier;
              const isNew = v.id !== p.id && !!v.variant_parent_id && v.variant_state !== 'provisional';
              const proposer = suppliers.find(s => s.id === v.proposed_by);
              const busy = varBusy === v.id;
              return (
                <Card key={v.id} style={{ marginBottom: 6, borderColor: isPending ? T.warn + '70' : v.id === p.id ? T.accent + '70' : T.border, animation: flashVarId === v.id ? 'mcFlashCard 0.7s ease-in-out 3' : undefined }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <span style={{ fontWeight: 700, color: T.text, fontSize: 14 }}>{v.name}</span>
                      <span style={{ fontFamily: mono, fontSize: 11, color: T.dim, marginLeft: 8 }}>{v.sku}</span>
                      {v.id === p.id && <span style={{ marginLeft: 8 }}><SBadge label={lang === 'zh' ? '当前' : 'Viewing'} color={T.accent} bg={T.surfaceAlt} /></span>}
                      {isPending && <span style={{ marginLeft: 8 }}><SBadge label={lang === 'zh' ? '待审核' : 'Suggested'} color={T.warn} bg={T.warnBg} /></span>}
                      {isNew && <span style={{ marginLeft: 8 }}><SBadge label={lang === 'zh' ? '已接受' : 'Accepted'} color={T.success} bg={T.successBg} /></span>}
                      {isPending && (
                        <div style={{ fontSize: 12, color: T.muted, marginTop: 4 }}>
                          {lang === 'zh' ? '由 ' : 'Suggested by '}<strong style={{ color: T.text }}>{proposer?.name || v.proposed_by}</strong>
                          {v.proposed_at ? ` · ${String(v.proposed_at).slice(0, 16)}` : ''}
                          {b.price != null ? ` · ${lang === 'zh' ? '其报价' : 'their price'} {money(b.price)}` : ''}
                        </div>
                      )}
                      {v.variant_note && <div style={{ fontSize: 12, color: T.orange, marginTop: 4 }}>“{v.variant_note}”</div>}
                      {!isPending && (
                        <div style={{ fontSize: 12, color: T.muted, marginTop: 4 }}>
                          {b.count}/{b.total} {Lk('quoted')}
                          {b.price != null && <span style={{ fontFamily: mono, color: T.success, fontWeight: 700, marginLeft: 8 }}>{money(b.price)} {Lk('best')}</span>}
                          {isNew && v.opened_at && <span style={{ marginLeft: 8, color: T.dim }}>{lang === 'zh' ? '接受于 ' : 'accepted '}{String(v.opened_at).slice(0, 10)}</span>}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      {isPending ? (
                        <>
                          {renameVals[v.id] !== undefined ? (
                            <>
                              <input autoFocus value={renameVals[v.id]} onChange={e => setRenameVals({ ...renameVals, [v.id]: e.target.value })}
                                onKeyDown={e => { if (e.key === 'Escape') setRenameVals(prev => { const n = { ...prev }; delete n[v.id]; return n; }); }}
                                placeholder={lang === 'zh' ? '产品名称' : 'Product name'}
                                style={{ fontFamily: font, fontSize: 12, padding: '6px 9px', width: 230, background: T.bg, border: '1px solid ' + T.accent, borderRadius: 6, color: T.text, outline: 'none' }} />
                              <Btn variant="small" disabled={busy} onClick={() => doRenameVariant(v)}>{IcCheck} {Lk('save')}</Btn>
                              <Btn variant="ghostSm" onClick={() => setRenameVals(prev => { const n = { ...prev }; delete n[v.id]; return n; })}>{Lk('cancel')}</Btn>
                            </>
                          ) : (
                            <Btn variant="ghostSm" onClick={() => setRenameVals({ ...renameVals, [v.id]: v.name })}>{IcEdit} {lang === 'zh' ? '修改产品名称' : 'Change product name'}</Btn>
                          )}
                          <Btn variant="small" disabled={busy} onClick={() => doOpenVariant(v)}>{lang === 'zh' ? '开放给所有供应商' : 'Open to all suppliers'}</Btn>
                          <Btn variant="dangerSm" disabled={busy} onClick={() => doRejectVariant(v)}>{lang === 'zh' ? '不需要' : 'Not needed'}</Btn>
                        </>
                      ) : v.id !== p.id && v.status === 'quoting' ? (
                        <Btn variant="ghostSm" onClick={() => { setDetailId(v.id); setAssignQty(''); setVarMsg(null); }}>{lang === 'zh' ? '查看' : 'View'}</Btn>
                      ) : v.id !== p.id ? (
                        <StatusBadge status={v.status} lang={lang} />
                      ) : null}
                    </div>
                  </div>
                </Card>
              );
            })}
            {pendingHere.length > 0 && (
              <div style={{ fontSize: 12, color: T.muted, marginTop: 8, lineHeight: 1.6 }}>
                {lang === 'zh'
                  ? '接受后，该版本会成为独立产品，出现在每个供应商的待报价列表中（提交者的价格已填入），无需重新发送报价请求。名称先改好——以我们的写法为准。'
                  : 'Accepting one turns it into a product of its own, out for quoting to every supplier — the supplier who suggested it already has a price on it, and no request is re-sent. Fix the name first: ours is the one that sticks.'}
              </div>
            )}
          </Sect>
        )}

        {/* Restrict to supplier */}
        <Sect title={lang === 'zh' ? '限制供应商（可选）' : 'Restrict to Supplier (Optional)'}>
          <Card>
            <div style={{ display: 'flex', alignItems: 'end', gap: 12 }}>
              <Sel label={lang === 'zh' ? '仅此供应商可报价' : 'Only this supplier can quote'} value={p.restrict_supplier || ''} onChange={e => doRestrict(p.id, e.target.value)} style={{ minWidth: 240 }}>
                <option value="">{Lk('allSuppliers')}</option>
                {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </Sel>
            </div>
          </Card>
        </Sect>

        {/* Assign quantity */}
        <Sect title={Lk('assignQty')}>
          <Card>
            <Inp label={lang === 'zh' ? '要订购的数量 *' : 'Quantity to order *'} placeholder="e.g. 100" type="number" value={assignQty} onChange={e => setAssignQty(e.target.value)} style={{ maxWidth: 200, borderColor: qtyFlash ? T.danger : undefined, boxShadow: qtyFlash ? `0 0 8px ${T.danger}60` : undefined, transition: 'all 0.15s' }} />
          </Card>
        </Sect>

        {/* Live Supplier Quotes */}
        <Sect title={lang === 'zh' ? '实时供应商报价' : 'Live Supplier Quotes'}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 13, fontFamily: font }}>
              <thead>
                <tr>
                  {[Lk('supplier'), Lk('price'), Lk('status'), Lk('action')].map((h, i) => (
                    <th key={i} style={{ textAlign: i === 0 ? 'left' : 'center', padding: '10px 14px', background: T.surfaceAlt, borderBottom: `1px solid ${T.border}`, color: T.dim, fontSize: 10, fontWeight: 800, textTransform: 'uppercase', borderRadius: i === 0 ? '8px 0 0 0' : i === 3 ? '0 8px 0 0' : 0 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {relevantSup.map(s => {
                  const q = p.quotes?.[s.id];
                  const isBest = bestSup === s.id;
                  const hasValidQuote = q?.available && q?.price != null;
                  const inlinePrice = quoteInputs[s.id] || '';
                  return (
                    <tr key={s.id} style={{ background: hoveredId === s.id ? '#EFF6FF' : 'transparent' }} onMouseEnter={() => setHoveredId(s.id)} onMouseLeave={() => setHoveredId(null)}>
                      <td style={{ padding: '12px 14px', borderBottom: `1px solid ${T.border}`, fontWeight: 700, color: T.text }}>{s.name}</td>
                      <td style={{ textAlign: 'center', padding: '12px 14px', borderBottom: `1px solid ${T.border}`, fontFamily: mono, fontSize: 14, fontWeight: 700, color: !q ? T.dim : !q.available ? T.danger : isBest ? T.successBr : T.text, background: isBest ? T.successBg : 'transparent' }}>
                        {hasValidQuote ? (
                          <>
                            {money(q.price)}
                            {isBest && <div style={{ fontSize: 9, fontFamily: font, fontWeight: 800, color: T.success, marginTop: 2, textTransform: 'uppercase' }}>{Lk('best')}</div>}
                          </>
                        ) : (
                          <input
                            type="number"
                            placeholder="0.00"
                            value={inlinePrice}
                            onChange={e => setQuoteInputs(prev => ({ ...prev, [s.id]: e.target.value }))}
                            onKeyDown={e => { if (e.key === 'Enter') doSubmitQuoteFor(p.id, s.id, inlinePrice); }}
                            style={{ width: 80, fontFamily: mono, fontSize: 12, padding: '4px 8px', background: T.bg, border: `1px solid ${T.border}`, borderRadius: 5, color: T.text, outline: 'none', textAlign: 'center' }}
                          />
                        )}
                      </td>
                      <td style={{ textAlign: 'center', padding: '12px 14px', borderBottom: `1px solid ${T.border}` }}>
                        {!q ? <span style={{ color: T.dim, fontStyle: 'italic', fontSize: 12 }}>{Lk('awaiting')}</span>
                          : !q.available ? <SBadge label={Lk('unavailable')} color={T.danger} bg={T.dangerBg} />
                          : <SBadge label={Lk('quoted')} color={T.success} bg={T.successBg} />}
                      </td>
                      <td style={{ textAlign: 'center', padding: '12px 14px', borderBottom: `1px solid ${T.border}` }}>
                        {hasValidQuote ? (
                          <Btn variant="small" onClick={() => doAssign(p.id, s.id)}>{Lk('assign')}</Btn>
                        ) : (
                          inlinePrice && parseFloat(inlinePrice) > 0 && (
                            <Btn variant="small" onClick={() => doSubmitQuoteFor(p.id, s.id, inlinePrice)}>
                              {lang === 'zh' ? '提交' : 'Submit'}
                            </Btn>
                          )
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Sect>

        {/* Notes from suppliers on this row, and a reply to one of them. A supplier sees only the
            notes addressed to it; our own internal notes on the product stay internal. */}
        <Sect title={`${lang === 'zh' ? '供应商备注' : 'Supplier notes'}${prodConvos.length ? ` (${prodConvos.length})` : ''}`}>
          <style>{`@keyframes mcNotePulse {
            0%, 100% { background: ${T.surface}; box-shadow: 0 0 0 0 ${T.danger}00; }
            50% { background: ${T.dangerBg}; box-shadow: 0 0 0 4px ${T.danger}33; }
          }`}</style>
          {prodConvos.length === 0 ? (
            <div style={{ color: T.dim, fontSize: 13, fontStyle: 'italic' }}>{lang === 'zh' ? '此产品暂无备注。' : 'No notes on this item.'}</div>
          ) : prodConvos.map(c => {
            const sup = suppliers.find(s => s.id === c.supplier_id);
            const fromUs = c.author_type === 'admin';
            return (
              <div key={c.id} style={{
                background: fromUs ? T.surfaceAlt : T.surface,
                border: `1px solid ${fromUs ? T.border : T.danger}`,
                borderRadius: 8, padding: '8px 12px', marginBottom: 6,
                // A note from a supplier keeps blinking until the row leaves quoting: it is the one
                // thing here nobody is looking for, and missing it is what this whole card is about.
                animation: fromUs ? undefined : 'mcNotePulse 2.4s ease-in-out infinite',
              }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: fromUs ? T.dim : T.accent, textTransform: 'uppercase' }}>
                  {fromUs ? (lang === 'zh' ? '我们' : 'Us') : (sup?.name || c.author)}
                  {fromUs && sup && <span style={{ color: T.muted }}> → {sup.name}</span>}
                  <span style={{ color: T.dim, marginLeft: 8, fontFamily: mono, fontWeight: 600 }}>{String(c.created_at || '').slice(0, 16)}</span>
                </div>
                <div style={{ fontSize: 13, color: T.text, whiteSpace: 'pre-wrap', marginTop: 2 }}>{c.text}</div>
              </div>
            );
          })}
          <Card style={{ marginTop: 8 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <Sel label={lang === 'zh' ? '回复给' : 'Reply to'} value={replyTo} onChange={e => setReplyTo(e.target.value)} style={{ minWidth: 200 }}>
                <option value="">{lang === 'zh' ? '选择供应商…' : 'Choose a supplier…'}</option>
                {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </Sel>
              <div style={{ flex: 1, minWidth: 220 }}>
                <textarea value={replyText} onChange={e => setReplyText(e.target.value)} rows={2}
                  placeholder={lang === 'zh' ? '只有该供应商能看到此回复' : 'Only that supplier sees this reply'}
                  style={{ width: '100%', boxSizing: 'border-box', fontFamily: font, fontSize: 13, padding: '8px 10px', background: T.bg, border: '1px solid ' + T.border, borderRadius: 6, color: T.text, outline: 'none', resize: 'vertical' }} />
              </div>
              <Btn variant="small" onClick={() => sendReply(p.id)}>{lang === 'zh' ? '发送' : 'Send'}</Btn>
            </div>
          </Card>
        </Sect>

        {/* Order History */}
        <Sect title={Lk('orderHistory')}>
          {orderHistory.length === 0 ? (
            <div style={{ color: T.dim, fontSize: 13, fontStyle: 'italic' }}>{Lk('noData')}</div>
          ) : (
            <div style={{ maxHeight: 300, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: font }}>
                <thead>
                  <tr>
                    {[lang === 'zh' ? '日期' : 'Date', Lk('supplier'), Lk('price'), Lk('qty')].map((h, i) => (
                      <th key={i} style={{ textAlign: 'left', padding: '8px 12px', background: T.surfaceAlt, borderBottom: `1px solid ${T.border}`, color: T.dim, fontSize: 10, fontWeight: 800, textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {orderHistory.map((oh, i) => (
                    <tr key={i}>
                      <td style={{ padding: '8px 12px', borderBottom: `1px solid ${T.border}`, fontFamily: mono, fontSize: 12, color: T.muted }}>{oh.date}</td>
                      <td style={{ padding: '8px 12px', borderBottom: `1px solid ${T.border}`, color: T.text }}>{oh.supplier_name || '—'}</td>
                      <td style={{ padding: '8px 12px', borderBottom: `1px solid ${T.border}`, fontFamily: mono, color: T.accent }}>{oh.price ? `${money(oh.price)}` : '—'}</td>
                      <td style={{ padding: '8px 12px', borderBottom: `1px solid ${T.border}`, fontFamily: mono }}>{oh.qty || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Sect>
      </div>
    );
  }

  // List view
  return (
    <div>
      <h2 style={{ margin: '0 0 22px', fontSize: 22, fontWeight: 900, color: T.text }}>{Lk('quoting')} ({quotingP.length})</h2>

      {quotingP.length === 0 ? (
        <div style={{ color: T.dim, fontSize: 13, fontStyle: 'italic', padding: 20 }}>{lang === 'zh' ? '目前没有报价中的产品。取消勾选产品以开始。' : 'No products in quoting. Untick a product to start.'}</div>
      ) : (
        <>
          <SearchBar sticky value={search} onChange={setSearch} placeholder={Lk('searchProduct')} style={{ marginBottom: 12 }} />
          {filtered.map(p => {
            const qc = Object.values(p.quotes || {}).filter(q => q.available && q.price != null).length;
            const sugg = p.group_id ? awaitingVariants.filter(v => v.group_id === p.group_id).length : 0;
            return (
              <Card key={p.id} hoverBg onClick={() => setDetailId(p.id)} style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <span style={{ fontWeight: 700, color: T.text, fontSize: 14 }}>{p.name}</span>
                    <span style={{ fontFamily: mono, fontSize: 11, color: T.dim, marginLeft: 10 }}>{p.sku}</span>
                    {p.cost_price != null && <span style={{ fontSize: 11, color: T.muted, marginLeft: 10 }}>{Lk('cost')}: {money(p.cost_price)}</span>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 12, color: T.muted }}>{qc}/{suppliers.length} {Lk('quoted')}</span>
                    {p.restrict_supplier && <span style={{ fontSize: 11, color: T.warn, fontWeight: 700 }}>{Lk('restricted')}</span>}
                    {sugg > 0 && <SBadge label={lang === 'zh' ? `${sugg} 个建议版本` : `${sugg} suggested version${sugg > 1 ? 's' : ''}`} color={T.warn} bg={T.warnBg} />}
                    {p.variant_parent_id && <SBadge label={lang === 'zh' ? '版本' : 'Version'} color={T.success} bg={T.successBg} />}
                    <StatusBadge status="quoting" lang={lang} />
                  </div>
                </div>
              </Card>
            );
          })}
          {filtered.length === 0 && search && (
            <div style={{ color: T.dim, fontSize: 13, fontStyle: 'italic', padding: 20 }}>{Lk('noResults')}</div>
          )}
        </>
      )}
    </div>
  );
}
